import { memo, type ReactNode, useEffect } from 'react'
import { createPortal } from 'react-dom'
import { Button } from '../../atoms'

interface ModalProps {
  isOpen: boolean
  onClose: () => void
  title: ReactNode
  children: ReactNode
  footer?: ReactNode
  className?: string
}

export const Modal = memo(({ isOpen, onClose, title, children, footer, className }: ModalProps) => {
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden'
    } else {
      document.body.style.overflow = ''
    }
    return () => {
      document.body.style.overflow = ''
    }
  }, [isOpen])

  if (!isOpen) {
    return null
  }

  return createPortal(
    <div
      className="modal-overlay"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      onKeyDown={e => {
        if (e.key === 'Escape') {
          onClose()
        }
      }}
      tabIndex={-1}>
      <div
        className={`modal ${className ?? ''}`.trim()}
        onClick={e => e.stopPropagation()}
        onKeyDown={e => e.stopPropagation()}
        role="document">
        <header className="modal__header">
          <h2>{title}</h2>
          <Button
            variant="ghost"
            className="modal__close"
            onClick={onClose}
            aria-label="Close modal">
            &times;
          </Button>
        </header>
        <div className="modal__body">{children}</div>
        {footer && <footer className="modal__footer">{footer}</footer>}
      </div>
    </div>,
    document.body,
  )
})

Modal.displayName = 'Modal'
